import React from 'react';
import PropTypes from 'prop-types';
import { RefreshCw } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import { cn } from 'src/lib/utils';

// Underline-style tab bar for the Assignment Pipeline — a page-specific
// look (active tab: brand text + 2px underline; count as a neutral pill),
// distinct from the app-wide FilterButton pill style used elsewhere. Counts
// are optional per tab: a tab renders no badge at all if its count isn't in
// `counts`, rather than showing a misleading 0.
//
// `onRefresh` is optional — only AssignmentPipeline.jsx's own instance
// passes it today, rendering a refresh icon button inside this same
// bordered bar (right-aligned via justify-between) rather than as a
// separate floating control next to it, per explicit request.
const PipelineTabBar = ({
  filters,
  activeFilter,
  onFilterChange,
  counts,
  onRefresh,
  isRefreshing,
}) => (
  <div className="flex items-center justify-between gap-1 border border-border rounded-t-lg bg-card px-2">
    <div className="flex items-center gap-1">
      {filters.map((filter) => {
        const isActive = filter.value === activeFilter;
        const count = counts?.[filter.value];
        return (
          <button
            key={filter.value}
            type="button"
            onClick={() => onFilterChange(filter.value)}
            className={cn(
              'relative flex items-center gap-2 px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors',
              isActive
                ? 'text-primary'
                : 'text-muted-foreground hover:text-foreground-strong'
            )}
          >
            {filter.label}
            {count !== undefined && (
              <span
                className={cn(
                  'inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold',
                  isActive
                    ? 'bg-brand-bg-subtle text-primary'
                    : 'bg-muted text-muted-foreground'
                )}
              >
                {count}
              </span>
            )}
            {isActive && (
              <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-primary rounded-full" />
            )}
          </button>
        );
      })}
    </div>
    {onRefresh && (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRefresh}
        disabled={isRefreshing}
        aria-label="Refresh candidate list"
        title="Refresh candidate list"
        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground-strong"
      >
        <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
      </Button>
    )}
  </div>
);

PipelineTabBar.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  activeFilter: PropTypes.string,
  onFilterChange: PropTypes.func,
  counts: PropTypes.objectOf(PropTypes.number),
  onRefresh: PropTypes.func,
  isRefreshing: PropTypes.bool,
};

PipelineTabBar.defaultProps = {
  activeFilter: null,
  onFilterChange: () => {},
  counts: null,
  onRefresh: null,
  isRefreshing: false,
};

export default PipelineTabBar;
